import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useQueries,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
  Message,
  PageForm,
} from '@components';
import { GlobalContext } from '@contexts';
import { usePageTemplates } from '@hooks';
import {
  businessQuery,
  datamodelQuery,
  pageDeleteMutation,
  pageMutation,
  pageQuery,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { TemplateBreadcrumbs } from '../components/TemplateBreadcrumbs/TemplateBreadcrumbs.jsx';
import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

const TemplatePageWrapper = ({ pageData, siteData, businessID, ...props }) => {
  // load some additional data needed to render the page form for a template page
  const { data: pageTemplates, isPending: pageTemplatesIsPending } =
    usePageTemplates(siteData.id, siteData.theme_id);

  // filter templates to the one tied to this page
  const filteredPageTemplates = !pageTemplatesIsPending
    ? pageTemplates.filter((p) => p.template_name == pageData.custom_page_type)
    : [];

  // get the datamodel
  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(
      !pageTemplatesIsPending && filteredPageTemplates.length > 0
        ? filteredPageTemplates[0].datamodel_id
        : null,
    ),
  );

  // get all the related datamodels used on fields
  const relatedDatamodels = useQueries({
    enabled: !datamodelIsPending,
    queries: datamodelData
      ? datamodelData.fields
          .filter((field) => Number.isInteger(field.related_datamodel_id))
          .map((field) => datamodelQuery(field.related_datamodel_id))
      : [],
  });
  // are all datamodel queries finished
  const allFinished = relatedDatamodels.every((query) => query.isSuccess);

  if (!pageTemplatesIsPending && filteredPageTemplates.length == 0) {
    return (
      <Message
        messageStyle="error"
        title="Page template error"
        message={`Can't find matching Page template for ${pageData.custom_page_type}`}
        fadeoutMessage={false}
        isFormMessage={false}
      />
    );
  }

  return (
    <>
      {(pageTemplatesIsPending || datamodelIsPending || !allFinished) && (
        <Loader className="sb-loader sb-loader-large" />
      )}
      {pageTemplates && datamodelData && allFinished && (
        <PageForm
          {...props}
          pageTemplate={filteredPageTemplates[0]}
          datamodelData={datamodelData}
          relatedDatamodels={relatedDatamodels}
          pageData={pageData}
          siteData={siteData}
        />
      )}
    </>
  );
};

export const TemplatePage = () => {
  const { t, i18n } = useTranslation();
  const { businessID, pageID, siteID } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const globals = useContext(GlobalContext);

  // get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // get site/template
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get page
  const {
    data: pageData,
    isPending: pageIsPending,
    isError,
  } = useQuery(pageQuery(pageID));

  const onPageSaveSuccess = () => {
    // invalidate the lists so its purged
    queryClient.invalidateQueries({ queryKey: ['pages', 'list'] });
    // invalidate the detail for revisits
    queryClient.invalidateQueries({ queryKey: ['pages', 'detail', pageID] });
  };

  // save page
  const mutation = useMutation(
    pageMutation(pageID, {
      onSuccess: () => onPageSaveSuccess(),
    }),
  );

  // publish page
  const publishMutation = useMutation(
    pageMutation(pageID, {
      onSuccess: () => onPageSaveSuccess(),
    }),
  );

  // delete page
  const deleteMutation = useMutation(
    pageDeleteMutation(pageID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['pages', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/projects/${businessID}/templates/${siteID}/pages`);
      },
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <TemplateBreadcrumbs businessData={businessData} siteData={siteData}>
            <li className="sb-flex">
              <Link
                to={`/dashboard/projects/${businessID}/templates/${siteID}/pages`}
              >
                {t('Pages')}
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {pageIsPending && <span>...</span>}
              {pageData && <span>{pageData.name}</span>}
            </li>
          </TemplateBreadcrumbs>
          <div className="sb-manage-content">
            {(siteIsPending || pageIsPending) && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {siteData && pageData && !isError && (
              <>
                <h1>{pageData.name}</h1>
                {pageData.type == 'template_page' && (
                  <TemplatePageWrapper
                    disabled={mutation.isPending || publishMutation.isPending}
                    mutation={mutation}
                    publishMutation={publishMutation}
                    pageData={pageData}
                    siteData={siteData}
                    businessID={businessID}
                  />
                )}
                <div className="sb-message-action-section">
                  <ActionObject
                    onAction={() => deleteMutation.mutate()}
                    loading={deleteMutation.isPending}
                    actionName="Delete"
                    actionVerb="Deleting"
                    label="Delete page"
                    note="This page will be permanently deleted"
                  />
                </div>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
